import { useState, useEffect } from 'react';
import { User, Building2, Bell, Palette, Save, CheckCircle, AlertCircle, Loader2, AlertTriangle, Mail, CreditCard, GraduationCap, ArrowRight, Shield, Inbox, Users, Pencil, X, KeyRound } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';
import { createPortalSession } from '../lib/billingApi';
import { CustomSelect } from '../components/CustomSelect';
import { useDarkMode } from '../context/DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { api } from '../lib/api';
import { MailMonitorSettings } from '../components/MailMonitorSettings';
import TeamSettings from '../components/TeamSettings';
import ApiSettings from '../components/ApiSettings';
import AutomationSettings from '../components/AutomationSettings';
import ConfirmDialog from '../components/admin/ConfirmDialog';

export default function Settings() {
  const { darkMode, toggleDarkMode, themeMode, setThemeMode } = useDarkMode();
  const { user, updateProfile } = useAuth();
  const { status: subStatus, loading: subLoading } = useSubscriptionStatus();

  // Profile state
  const [contactPerson, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [advisorNumber, setAdvisorNumber] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyHausnummer, setCompanyHausnummer] = useState('');
  const [companyPlz, setCompanyPlz] = useState('');
  const [companyOrt, setCompanyOrt] = useState('');
  const [phone, setPhone] = useState('');
  const [beraterAnrede, setBeraterAnrede] = useState('');
  const [vollmachtOtherEnabled, setVollmachtOtherEnabled] = useState(false);
  const [vollmachtOtherAnrede, setVollmachtOtherAnrede] = useState('');
  const [vollmachtOtherVorname, setVollmachtOtherVorname] = useState('');
  const [vollmachtOtherNachname, setVollmachtOtherNachname] = useState('');
  const [vollmachtOtherCompany, setVollmachtOtherCompany] = useState('');
  const [vollmachtOtherAddress, setVollmachtOtherAddress] = useState('');
  const [vollmachtOtherPlz, setVollmachtOtherPlz] = useState('');
  const [vollmachtOtherOrt, setVollmachtOtherOrt] = useState('');
  const [vollmachtOtherPhone, setVollmachtOtherPhone] = useState('');
  const [vollmachtOtherEmail, setVollmachtOtherEmail] = useState('');
  const [filenameBlocks, setFilenameBlocks] = useState<string[]>([]);
  const [notifySignedUploadEmail, setNotifySignedUploadEmail] = useState(true);
  const [notifyPayoutDigest, setNotifyPayoutDigest] = useState(true);
  // Antwortadresse für Kundenmails (Team-weit). Wird aus /api/team geladen, weil sie
  // am Team hängt und nicht am Profil.
  const [replyToEmail, setReplyToEmail] = useState('');
  const [replyToOriginal, setReplyToOriginal] = useState('');
  const [replyToSaving, setReplyToSaving] = useState(false);
  // Team-weites Opt-in: Mail-Team-Mitglieder benachrichtigen, wenn eine Mail nicht
  // zugeordnet werden konnte. Wie replyToEmail team-weit, nur der Inhaber ändert es.
  const [notifyUnassignedMail, setNotifyUnassignedMail] = useState(true);
  const [notifyUnassignedMailOriginal, setNotifyUnassignedMailOriginal] = useState(true);
  const [notifyUnassignedMailSaving, setNotifyUnassignedMailSaving] = useState(false);
  // Ausgangs-Postfach des Teams (Versand über eigenen Mailserver statt Versanddienst).
  const [outgoing, setOutgoing] = useState<any | null>(null);
  const [outForm, setOutForm] = useState({ fromEmail: '', fromName: '', host: '', port: 465, username: '', password: '' });
  const [outSaving, setOutSaving] = useState(false);
  const [outTesting, setOutTesting] = useState(false);
  const [outTestResult, setOutTestResult] = useState<{ ok: boolean; error?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [searchParams] = useSearchParams();
  const checkoutSuccess = searchParams.get('checkout') === 'success';
  const [portalLoading, setPortalLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState<string | null>(null);

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      await createPortalSession();
    } catch (err: any) {
      setAlertMsg(err.message || 'Fehler beim Öffnen des Portals');
    } finally {
      setPortalLoading(false);
    }
  };

  // Tab navigation — initial tab from URL hash (e.g. /settings#plan)
  type SettingsTab = 'profile' | 'plan' | 'notifications' | 'mail-monitor' | 'appearance' | 'admin' | 'account' | 'team' | 'api';
  const VALID_TABS: SettingsTab[] = ['profile', 'plan', 'notifications', 'mail-monitor', 'appearance', 'admin', 'account', 'team', 'api'];
  const parseHash = () => window.location.hash.slice(1).split('?')[0] as SettingsTab;
  const initialTab = (typeof window !== 'undefined' && VALID_TABS.includes(parseHash()))
    ? parseHash()
    : 'profile';
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);

  // React to hash changes after mount (when user navigates within /settings).
  useEffect(() => {
    const onHashChange = () => {
      const next = parseHash();
      if (VALID_TABS.includes(next)) setActiveTab(next);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Admin Subscription State
  const [adminTargetEmail, setAdminTargetEmail] = useState('');
  const [adminTargetId, setAdminTargetId] = useState<number | null>(null);
  const [adminPlan, setAdminPlan] = useState('free');
  const [adminPlanExpiresAt, setAdminPlanExpiresAt] = useState('');
  const [adminReportCredits, setAdminReportCredits] = useState(0);
  const [adminStartklar, setAdminStartklar] = useState(false);
  const [adminStartklarExpiresAt, setAdminStartklarExpiresAt] = useState('');
  const [adminMaxReports, setAdminMaxReports] = useState<number | null>(null);
  const [adminMaxSeats, setAdminMaxSeats] = useState<number | null>(null);
  const [adminSubSaving, setAdminSubSaving] = useState(false);
  const [adminSubMessage, setAdminSubMessage] = useState('');

  // App settings state
  // theme state uses context's themeMode directly
  const [fontSize, setFontSize] = useState('Mittel');
  const [language, setLanguage] = useState('Deutsch');

  // Pre-fill profile fields
  useEffect(() => {
    if (user) {
      setFirstName(user.contactPerson || '');
      setEmail(user.email || '');
      setJobTitle(user.jobTitle || '');
      setAdvisorNumber(user.advisorNumber || '');
      setCompanyName(user.companyName || '');
      setCompanyLogoUrl(user.companyLogoUrl || '');
      setPhone(user.phone || '');
      setCompanyAddress(user.companyAddress || '');
      setCompanyHausnummer((user as any).companyHausnummer || '');
      setCompanyPlz((user as any).companyPlz || '');
      setCompanyOrt((user as any).companyOrt || '');
      setBeraterAnrede((user as any).beraterAnrede || '');
      setVollmachtOtherEnabled((user as any).vollmachtOtherEnabled || false);
      setVollmachtOtherAnrede((user as any).vollmachtOtherAnrede || '');
      setVollmachtOtherVorname((user as any).vollmachtOtherVorname || '');
      setVollmachtOtherNachname((user as any).vollmachtOtherNachname || '');
      setVollmachtOtherCompany((user as any).vollmachtOtherCompany || '');
      setVollmachtOtherAddress((user as any).vollmachtOtherAddress || '');
      setVollmachtOtherPlz((user as any).vollmachtOtherPlz || '');
      setVollmachtOtherOrt((user as any).vollmachtOtherOrt || '');
      setVollmachtOtherPhone((user as any).vollmachtOtherPhone || '');
      setVollmachtOtherEmail((user as any).vollmachtOtherEmail || '');
      setFilenameBlocks(((user as any).filenamePattern as string | null)?.match(/\{[^}]+\}/g) ?? []);
      setNotifySignedUploadEmail((user as any).notifySignedUploadEmail !== false);
      setNotifyPayoutDigest((user as any).notifyPayoutDigest !== false);
    }
  }, [user]);

  // Team-Antwortadresse laden. Scheitert still (z. B. kein Team) — dann bleibt das
  // Feld leer und es gilt weiterhin die Adresse des jeweiligen Beraters.
  useEffect(() => {
    if (!user?.teamId) return;
    api.get<{ replyToEmail: string | null; notifyUnassignedMail: boolean }>('/api/team')
      .then(team => {
        setReplyToEmail(team.replyToEmail ?? '');
        setReplyToOriginal(team.replyToEmail ?? '');
        setNotifyUnassignedMail(team.notifyUnassignedMail !== false);
        setNotifyUnassignedMailOriginal(team.notifyUnassignedMail !== false);
      })
      .catch(() => {});
    api.get<any | null>('/api/team/outgoing-mail')
      .then(acc => {
        setOutgoing(acc);
        if (acc) setOutForm({ fromEmail: acc.fromEmail ?? '', fromName: acc.fromName ?? '', host: acc.host ?? '', port: acc.port ?? 465, username: acc.username ?? '', password: '' });
      })
      .catch(() => {});
  }, [user?.teamId]);

  // Auto-dismiss success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleThemeChange = (val: string) => {
    setThemeMode(val.toLowerCase() as 'hell' | 'dunkel' | 'system');
  };

  const themeDisplay = themeMode === 'hell' ? 'Hell' : themeMode === 'dunkel' ? 'Dunkel' : 'System';
  const startAppearanceEdit = () => {
    setPendingTheme(themeDisplay);
    setPendingFontSize(fontSize);
    setPendingLanguage(language);
    setErrorMessage('');
    setSuccessMessage('');
    setEditingAppearance(true);
  };
  const saveAppearance = () => {
    handleThemeChange(pendingTheme);
    setFontSize(pendingFontSize);
    setLanguage(pendingLanguage);
    setSuccessMessage('Darstellung gespeichert');
    setEditingAppearance(false);
  };

  // Pro Block ein eigener Bearbeiten/Speichern-Modus statt einer globalen Leiste.
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingOrg, setEditingOrg] = useState(false);
  const [editingNotif, setEditingNotif] = useState(false);
  const [editingFilename, setEditingFilename] = useState(false);
  const [editingAppearance, setEditingAppearance] = useState(false);
  // Theme wirkt live — daher im Edit-Modus erst „pending", Anwendung bei „Speichern".
  const [pendingTheme, setPendingTheme] = useState('System');
  const [pendingFontSize, setPendingFontSize] = useState('Mittel');
  const [pendingLanguage, setPendingLanguage] = useState('Deutsch');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);

  const saveProfileBlock = async () => {
    setSavingProfile(true);
    setSuccessMessage('');
    setErrorMessage('');
    try {
      await updateProfile({ contactPerson, email, phone, jobTitle, advisorNumber, beraterAnrede, vollmachtOtherEnabled, vollmachtOtherAnrede, vollmachtOtherVorname, vollmachtOtherNachname, vollmachtOtherCompany, vollmachtOtherAddress, vollmachtOtherPlz, vollmachtOtherOrt, vollmachtOtherPhone, vollmachtOtherEmail } as any);
      setSuccessMessage('Profil erfolgreich gespeichert');
      setEditingProfile(false);
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setSavingProfile(false);
    }
  };

  const cancelProfileEdit = () => {
    if (user) {
      setFirstName(user.contactPerson || '');
      setEmail(user.email || '');
      setJobTitle(user.jobTitle || '');
      setAdvisorNumber(user.advisorNumber || '');
      setPhone(user.phone || '');
      setBeraterAnrede((user as any).beraterAnrede || '');
      setVollmachtOtherEnabled((user as any).vollmachtOtherEnabled || false);
      setVollmachtOtherAnrede((user as any).vollmachtOtherAnrede || '');
      setVollmachtOtherVorname((user as any).vollmachtOtherVorname || '');
      setVollmachtOtherNachname((user as any).vollmachtOtherNachname || '');
      setVollmachtOtherCompany((user as any).vollmachtOtherCompany || '');
      setVollmachtOtherAddress((user as any).vollmachtOtherAddress || '');
      setVollmachtOtherPlz((user as any).vollmachtOtherPlz || '');
      setVollmachtOtherOrt((user as any).vollmachtOtherOrt || '');
      setVollmachtOtherPhone((user as any).vollmachtOtherPhone || '');
      setVollmachtOtherEmail((user as any).vollmachtOtherEmail || '');
    }
    setErrorMessage('');
    setEditingProfile(false);
  };

  const saveOrgBlock = async () => {
    setSavingOrg(true);
    setSuccessMessage('');
    setErrorMessage('');
    try {
      await updateProfile({ companyName, companyLogoUrl, companyAddress, companyHausnummer, companyPlz, companyOrt } as any);
      setSuccessMessage('Beratungsunternehmen gespeichert');
      setEditingOrg(false);
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setSavingOrg(false);
    }
  };

  const cancelOrgEdit = () => {
    if (user) {
      setCompanyName(user.companyName || '');
      setCompanyLogoUrl(user.companyLogoUrl || '');
      setCompanyAddress(user.companyAddress || '');
      setCompanyHausnummer((user as any).companyHausnummer || '');
      setCompanyPlz((user as any).companyPlz || '');
      setCompanyOrt((user as any).companyOrt || '');
    }
    setErrorMessage('');
    setEditingOrg(false);
  };

  // Dateiname-Muster wird im Team-Tab gespeichert (eigener Save, da nicht im Profil-Formular).
  const [savingFilename, setSavingFilename] = useState(false);
  const saveFilenamePattern = async () => {
    setSavingFilename(true);
    setSuccessMessage('');
    setErrorMessage('');
    try {
      await updateProfile({ filenamePattern: filenameBlocks.length > 0 ? filenameBlocks.join('_') : null } as any);
      setSuccessMessage('Dateiname-Muster gespeichert');
      setEditingFilename(false);
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setSavingFilename(false);
    }
  };

  const cancelFilenameEdit = () => {
    setFilenameBlocks(((user as any)?.filenamePattern as string | null)?.match(/\{[^}]+\}/g) ?? []);
    setErrorMessage('');
    setEditingFilename(false);
  };

  const searchUserByEmail = async () => {
    try {
      const users = await api.get<any[]>('/auth/admin/users');
      const found = Array.isArray(users) ? users.find((u: any) => u.email === adminTargetEmail) : null;
      if (!found) { setAdminSubMessage('User nicht gefunden'); return; }
      setAdminTargetId(found.id);
      setAdminPlan(found.plan ?? 'free');
      setAdminPlanExpiresAt(found.planExpiresAt ? found.planExpiresAt.slice(0, 10) : '');
      setAdminReportCredits(found.reportCredits ?? 0);
      setAdminStartklar(found.addonStartklar ?? false);
      setAdminStartklarExpiresAt(found.addonStartklarExpiresAt ? found.addonStartklarExpiresAt.slice(0, 10) : '');
      setAdminMaxReports(found.maxReportsOverride ?? null);
      setAdminMaxSeats(found.maxSeatsOverride ?? null);
      setAdminSubMessage(`User "${found.contactPerson || found.email}" geladen`);
    } catch {
      setAdminSubMessage('Fehler beim Laden');
    }
  };

  const saveAdminSubscription = async () => {
    if (!adminTargetId) return;
    setAdminSubSaving(true);
    try {
      await api.patch(`/auth/admin/users/${adminTargetId}`, {
        plan: adminPlan,
        planExpiresAt: adminPlanExpiresAt ? new Date(adminPlanExpiresAt).toISOString() : null,
        reportCredits: adminReportCredits,
        addonStartklar: adminStartklar,
        addonStartklarExpiresAt: adminStartklarExpiresAt ? new Date(adminStartklarExpiresAt).toISOString() : null,
        maxReportsOverride: adminMaxReports,
        maxSeatsOverride: adminMaxSeats,
      });
      setAdminSubMessage('Gespeichert');
    } catch {
      setAdminSubMessage('Fehler beim Speichern');
    }
    setAdminSubSaving(false);
  };

  const deletionRequestMailto = (() => {
    const subject = encodeURIComponent('Antrag auf Kontoloeschung');
    const body = encodeURIComponent(
      `Hallo StartKlar.Digital-Team,\n\nich beantrage hiermit die Loeschung meines Kontos gemaess Art. 17 DSGVO.\n\nKonto-E-Mail: ${user?.email ?? ''}\nName: ${user?.contactPerson ?? ''}\n\nVielen Dank.`,
    );
    return `mailto:info@startklar.digital?subject=${subject}&body=${body}`;
  })();

  const inputClasses = `w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    darkMode ? 'border-slate-600 bg-slate-700 text-white placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400'
  }`;

  const cardClasses = `rounded-lg border shadow-sm p-6 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;

  // Teammitglieder (Nicht-Owner) erben die Firmendaten vom Team-Owner und
  // können sie hier nicht bearbeiten. Persönliche Felder bleiben eigen.
  const orgManagedByOwner = !!user?.teamId && !!user?.teamRole && user.teamRole !== 'owner';
  // Die Antwortadresse gilt für das ganze Team — nur der Inhaber darf sie setzen.
  const istTeamInhaber = !user?.teamId || user?.teamRole === 'owner';
  // Tarif/Abo gilt fürs ganze Team — nur Owner/Admin dürfen ihn ändern (Mitglieder
  // nutzen ihn automatisch mit, ohne selbst zu bezahlen).
  const canManageBilling = !user?.teamId || user?.teamRole === 'owner' || user?.teamRole === 'admin';
  // Felder sind im Ansichtsmodus ausgegraut; erst „Bearbeiten" macht sie editierbar.
  const profileDisabled = !editingProfile;
  const orgDisabled = orgManagedByOwner || !editingOrg;
  const profileInputClasses = `${inputClasses} ${profileDisabled ? 'opacity-60 cursor-not-allowed' : ''}`;
  const orgInputClasses = `${inputClasses} ${orgDisabled ? 'opacity-60 cursor-not-allowed' : ''}`;

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-slate-900' : 'bg-slate-100'}`}>
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Einstellungen</h1>
          <p className={darkMode ? 'text-slate-400' : 'text-slate-600'}>
            Verwalten Sie Ihr Profil und Ihre Anwendungspräferenzen
          </p>
        </div>

        <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
          {/* Sidebar navigation */}
          {(() => {
            const TABS: Array<{ id: SettingsTab; label: string; Icon: typeof User; show?: boolean }> = [
              { id: 'profile', label: 'Profil', Icon: User },
              { id: 'plan', label: 'Tarif', Icon: CreditCard },
              { id: 'team', label: 'Team', Icon: Users },
              { id: 'notifications', label: 'Benachrichtigungen', Icon: Bell },
              { id: 'mail-monitor', label: 'Mail / Monitor', Icon: Inbox },
              { id: 'api', label: 'API & Automatisierung', Icon: KeyRound },
              { id: 'appearance', label: 'Darstellung', Icon: Palette },
              { id: 'admin', label: 'Admin', Icon: Shield, show: user?.isAdmin === true },
              { id: 'account', label: 'Konto', Icon: AlertTriangle },
            ];
            return (
              <aside className="md:w-56 md:flex-shrink-0">
                <nav className="md:sticky md:top-20 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
                  {TABS.filter(t => t.show !== false).map(({ id, label, Icon }) => {
                    const active = activeTab === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => {
                          setActiveTab(id);
                          if (typeof window !== 'undefined') {
                            window.history.replaceState(null, '', `#${id}`);
                          }
                        }}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap md:whitespace-normal text-left ${
                          active
                            ? darkMode
                              ? 'bg-blue-900/30 text-blue-300'
                              : 'bg-blue-50 text-blue-700'
                            : darkMode
                              ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                        }`}
                      >
                        <Icon className={`w-4 h-4 flex-shrink-0 ${active ? '' : 'opacity-70'}`} />
                        {label}
                      </button>
                    );
                  })}
                </nav>
              </aside>
            );
          })()}

          {/* Content column */}
          <div className="flex-1 min-w-0">

        {/* Messages */}
        {successMessage && (
          <div className="mb-6 flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">{successMessage}</span>
          </div>
        )}
        {errorMessage && (
          <div className="mb-6 flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-slate-800 rounded-lg text-red-700 dark:text-red-400">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">{errorMessage}</span>
          </div>
        )}

        {activeTab === 'profile' && (
        <div>
          <div className="space-y-6">
            {/* Profil */}
            <div className={cardClasses}>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="flex items-center gap-3 flex-1 min-w-0 basis-full sm:basis-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                    <User className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <div className="min-w-0">
                    <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Profil</h2>
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Ihre persönlichen Informationen</p>
                  </div>
                </div>
                {user?.teamId && (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                    user.teamRole === 'owner'
                      ? darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'
                      : darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
                  }`}>
                    <Users className="w-3 h-3" />
                    {(user as any).teamName ? `${(user as any).teamName} · ` : 'Team · '}
                    {user.teamRole === 'owner' ? 'Owner' : user.teamRole === 'admin' ? 'Admin' : 'Mitglied'}
                  </span>
                )}
                {!editingProfile && (
                  <button
                    type="button"
                    onClick={() => { setEditingProfile(true); setErrorMessage(''); setSuccessMessage(''); }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Bearbeiten
                  </button>
                )}
              </div>
              {user?.teamId && user.teamRole !== 'owner' && (
                <div className={`mb-4 rounded-lg px-4 py-3 ${darkMode ? 'bg-slate-700/50 border border-slate-600' : 'bg-slate-50 border border-slate-200'}`}>
                  <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Deine persönlichen Angaben pflegst du selbst. Die Firmendaten und
                    Vollmacht-Einstellungen werden vom Team-Owner verwaltet und gelten
                    für alle Teammitglieder.
                  </p>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Anrede</label>
                  <select value={beraterAnrede} onChange={e => setBeraterAnrede(e.target.value)} disabled={profileDisabled} className={profileInputClasses}>
                    <option value="">— wählen —</option>
                    <option value="Herr">Herr</option>
                    <option value="Frau">Frau</option>
                    <option value="Divers">Divers</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Ansprechpartner</label>
                  <input type="text" value={contactPerson} onChange={(e) => setFirstName(e.target.value)} placeholder="Max Mustermann" disabled={profileDisabled} className={profileInputClasses} />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>E-Mail-Adresse</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@firma.de" disabled={profileDisabled} className={profileInputClasses} />
                  <p className={`mt-1 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Mit dieser Adresse meldest du dich an.</p>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>BAFA-ID</label>
                  <input type="text" value={advisorNumber} onChange={(e) => setAdvisorNumber(e.target.value)} placeholder="z.B. 213100" disabled={profileDisabled} className={profileInputClasses} />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Telefon</label>
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+49 175 8641556" disabled={profileDisabled} className={profileInputClasses} />
                </div>

                {/* Vollmacht-Sektion */}
                <div className={`border-t pt-4 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                  {user?.teamId && user.teamRole !== 'owner' ? null : (<>
                  <div className={`rounded-lg px-4 py-3 mb-4 ${darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-100'}`}>
                    <p className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      Diese Person wird als Bevollmächtigte/r in die BAFA-Vollmacht eingetragen — sofern unten keine andere Person aktiviert ist.
                    </p>
                  </div>

                  <label className={`flex items-center gap-3 mb-1 ${profileDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={vollmachtOtherEnabled}
                      onChange={e => setVollmachtOtherEnabled(e.target.checked)}
                      disabled={profileDisabled}
                      className={`w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 ${profileDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <span className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                      Andere Person als Bevollmächtigte/r verwenden
                    </span>
                  </label>
                  <p className={`text-xs ml-7 mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Wenn aktiviert, werden die unten eingetragenen Daten in die Vollmacht eingetragen.
                  </p>

                  {vollmachtOtherEnabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Anrede</label>
                        <select value={vollmachtOtherAnrede} onChange={e => setVollmachtOtherAnrede(e.target.value)} disabled={profileDisabled} className={profileInputClasses}>
                          <option value="">— wählen —</option>
                          <option value="Herr">Herr</option>
                          <option value="Frau">Frau</option>
                          <option value="Divers">Divers</option>
                        </select>
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Vorname</label>
                        <input type="text" value={vollmachtOtherVorname} onChange={e => setVollmachtOtherVorname(e.target.value)} placeholder="Max" disabled={profileDisabled} className={profileInputClasses} />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Nachname</label>
                        <input type="text" value={vollmachtOtherNachname} onChange={e => setVollmachtOtherNachname(e.target.value)} placeholder="Mustermann" disabled={profileDisabled} className={profileInputClasses} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Firmenname</label>
                        <input type="text" value={vollmachtOtherCompany} onChange={e => setVollmachtOtherCompany(e.target.value)} placeholder="Musterfirma GmbH" disabled={profileDisabled} className={profileInputClasses} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Straße und Hausnummer</label>
                        <input type="text" value={vollmachtOtherAddress} onChange={e => setVollmachtOtherAddress(e.target.value)} placeholder="Musterstraße 1" disabled={profileDisabled} className={profileInputClasses} />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>PLZ</label>
                        <input type="text" value={vollmachtOtherPlz} onChange={e => setVollmachtOtherPlz(e.target.value)} placeholder="12345" disabled={profileDisabled} className={profileInputClasses} />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Ort</label>
                        <input type="text" value={vollmachtOtherOrt} onChange={e => setVollmachtOtherOrt(e.target.value)} placeholder="Berlin" disabled={profileDisabled} className={profileInputClasses} />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Telefon</label>
                        <input type="tel" value={vollmachtOtherPhone} onChange={e => setVollmachtOtherPhone(e.target.value)} placeholder="+49 123 456789" disabled={profileDisabled} className={profileInputClasses} />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>E-Mail</label>
                        <input type="email" value={vollmachtOtherEmail} onChange={e => setVollmachtOtherEmail(e.target.value)} placeholder="max@firma.de" disabled={profileDisabled} className={profileInputClasses} />
                      </div>
                    </div>
                  )}
                  </>)}
                </div>
              </div>
              {editingProfile && (
                <div className="flex justify-end gap-2 mt-6">
                  <button type="button" onClick={cancelProfileEdit} disabled={savingProfile} className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg font-medium transition-colors cursor-pointer disabled:opacity-50 ${darkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                    <X className="w-4 h-4" />
                    Abbrechen
                  </button>
                  <button type="button" onClick={saveProfileBlock} disabled={savingProfile} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
                    {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {savingProfile ? 'Wird gespeichert...' : 'Speichern'}
                  </button>
                </div>
              )}
            </div>

            {/* Beratungsunternehmen */}
            <div className={cardClasses}>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="flex items-center gap-3 flex-1 min-w-0 basis-full sm:basis-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-green-900/30' : 'bg-green-100'}`}>
                    <Building2 className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                  </div>
                  <div className="min-w-0">
                    <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Beratungsunternehmen</h2>
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Angaben zu Ihrer Organisation</p>
                  </div>
                </div>
                {!orgManagedByOwner && !editingOrg && (
                  <button
                    type="button"
                    onClick={() => { setEditingOrg(true); setErrorMessage(''); setSuccessMessage(''); }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Bearbeiten
                  </button>
                )}
              </div>
              {orgManagedByOwner && (
                <div className={`mb-4 flex items-start gap-2 rounded-lg px-4 py-3 ${darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-100'}`}>
                  <span className="text-blue-500 mt-0.5 shrink-0">ℹ️</span>
                  <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                    Diese Angaben werden vom Team-Owner verwaltet und erscheinen so auf
                    allen generierten Dokumenten.
                  </p>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Firmenname</label>
                  <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Name Ihres Unternehmens" disabled={orgDisabled} className={orgInputClasses} />
                </div>
                <div className="grid grid-cols-[1fr_auto] gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Straße</label>
                    <input type="text" value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)} placeholder="Musterstraße" disabled={orgDisabled} className={orgInputClasses} />
                  </div>
                  <div className="w-24">
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Nr.</label>
                    <input type="text" value={companyHausnummer} onChange={e => setCompanyHausnummer(e.target.value)} placeholder="1a" disabled={orgDisabled} className={orgInputClasses} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>PLZ</label>
                    <input type="text" value={companyPlz} onChange={e => setCompanyPlz(e.target.value)} placeholder="97980" disabled={orgDisabled} className={orgInputClasses} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Ort</label>
                    <input type="text" value={companyOrt} onChange={e => setCompanyOrt(e.target.value)} placeholder="Bad Mergentheim" disabled={orgDisabled} className={orgInputClasses} />
                  </div>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Logo URL</label>
                  <input type="url" value={companyLogoUrl} onChange={(e) => setCompanyLogoUrl(e.target.value)} placeholder="https://..." disabled={orgDisabled} className={orgInputClasses} />
                </div>
              </div>
              {editingOrg && (
                <div className="flex justify-end gap-2 mt-6">
                  <button type="button" onClick={cancelOrgEdit} disabled={savingOrg} className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg font-medium transition-colors cursor-pointer disabled:opacity-50 ${darkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                    <X className="w-4 h-4" />
                    Abbrechen
                  </button>
                  <button type="button" onClick={saveOrgBlock} disabled={savingOrg} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
                    {savingOrg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {savingOrg ? 'Wird gespeichert...' : 'Speichern'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        <div className="space-y-6 mt-6">
          {activeTab === 'plan' && (() => {
          /* Tarif & Nutzung */
            const PLAN_LABELS: Record<string, string> = {
              free: 'Free', pro: 'Pro', unlimited: 'Unlimited',
              starter: 'Starter', professional: 'Professional', business: 'Business', flat: 'Flat',
              partner: 'Partner',
            };
            const PLAN_COLORS: Record<string, string> = {
              free: darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600',
              pro: 'bg-blue-100 text-blue-700',
              unlimited: 'bg-purple-100 text-purple-700',
              starter: 'bg-emerald-100 text-emerald-700',
              professional: 'bg-blue-100 text-blue-700',
              business: 'bg-indigo-100 text-indigo-700',
              flat: 'bg-purple-100 text-purple-700',
              partner: 'bg-amber-100 text-amber-700',
            };
            const plan = subStatus?.effectivePlan ?? 'free';
            const planLabel = PLAN_LABELS[plan] ?? plan;
            const usage = subStatus?.reportUsage;
            const usageLimit = usage ? (usage.mode === 'pool' ? usage.available : usage.mode === 'unlimited' ? Infinity : usage.limit) : 0;
            const usageUsed = usage ? (usage.mode === 'pool' ? 0 : usage.used) : 0;
            const percentage = usage && Number.isFinite(usageLimit) && usageLimit > 0 ? Math.min((usageUsed / usageLimit) * 100, 100) : 0;
            const isAtLimit = usage ? (usage.mode === 'pool' ? usage.available <= 0 : usage.mode === 'unlimited' ? false : usageUsed >= usageLimit) : false;
            const expiresAt = subStatus?.planExpiresAt ? new Date(subStatus.planExpiresAt).toLocaleDateString('de-DE') : null;
            const startklarActive = subStatus?.addons?.startklar;
            const startklarExpires = subStatus?.addons?.startklarExpiresAt ? new Date(subStatus.addons.startklarExpiresAt).toLocaleDateString('de-DE') : null;

            return (
              <div className={cardClasses}>
                {checkoutSuccess && (
                  <div className="mb-4 p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                    <p className="font-semibold text-base mb-0.5">🎉 Upgrade erfolgreich!</p>
                    <p>Ihr <strong>{planLabel}</strong>-Plan ist jetzt aktiv. Alle Funktionen stehen Ihnen sofort zur Verfügung.</p>
                  </div>
                )}
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                    <CreditCard className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Tarif & Nutzung</h2>
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Ihr aktuelles Abonnement und Berichtskontingent</p>
                  </div>
                </div>

                {subLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Wird geladen…</div>
                ) : (
                  <div className="space-y-4">
                    {/* Plan badge + expiry */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${PLAN_COLORS[plan] ?? PLAN_COLORS.free}`}>
                        {planLabel}
                      </span>
                      {expiresAt && (
                        <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Läuft ab am {expiresAt}
                        </span>
                      )}
                      {startklarActive && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-700">
                          <GraduationCap className="w-3.5 h-3.5" /> Startklar-Programm{startklarExpires ? ` bis ${startklarExpires}` : ''}
                        </span>
                      )}
                    </div>

                    {/* Report usage */}
                    {usage && (
                      <div className="space-y-1.5">
                        {usage.mode === 'unlimited' ? (
                          <p className={`text-sm font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Unbegrenzt Berichte</p>
                        ) : (
                          <>
                            <div className="flex justify-between items-center text-sm">
                              <span className={darkMode ? 'text-slate-300' : 'text-slate-700'}>
                                {usage.mode === 'pool' ? 'Berichte verfügbar' : usage.mode === 'monthly' ? 'Berichte diesen Monat' : 'Berichte insgesamt'}
                              </span>
                              <span className={`font-medium ${isAtLimit ? 'text-red-500' : darkMode ? 'text-white' : 'text-slate-900'}`}>
                                {usage.mode === 'pool' ? usage.available : `${usage.used} / ${usage.limit}`}
                              </span>
                            </div>
                            <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                              <div
                                className={`h-2 rounded-full transition-all ${isAtLimit ? 'bg-red-500' : 'bg-blue-500'}`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            {isAtLimit && (
                              <p className="text-xs text-red-500">Limit erreicht – keine weiteren Berichte möglich</p>
                            )}
                          </>
                        )}
                        {usage.credits > 0 && (
                          <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            + {usage.credits} gekaufte/geschenkte Credits verfügbar
                          </p>
                        )}
                      </div>
                    )}

                    {/* Feature overview */}
                    {subStatus?.features && (
                      <div className={`grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1`}>
                        {[
                          { key: 'esf', label: 'ESF-Fragebogen' },
                          { key: 'templates', label: 'Vorlagen' },
                          { key: 'pdfExport', label: 'PDF-Export' },
                          { key: 'docxExport', label: 'Word-Export' },
                          { key: 'team', label: 'Team-Funktionen' },
                        ].map(({ key, label }) => {
                          const enabled = subStatus.features[key as keyof typeof subStatus.features];
                          return (
                            <div key={key} className={`flex items-center gap-2 text-xs ${enabled ? (darkMode ? 'text-slate-300' : 'text-slate-700') : (darkMode ? 'text-slate-600' : 'text-slate-400')}`}>
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${enabled ? 'bg-green-500' : (darkMode ? 'bg-slate-600' : 'bg-slate-300')}`} />
                              {label}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Upgrade CTA for free users */}
                    {plan === 'free' && canManageBilling && (
                      <div className={`mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg p-3 ${darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-100'}`}>
                        <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>Mehr Berichte mit einem kostenpflichtigen Tarif</p>
                        <Link to="/preise" className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap sm:ml-4">
                          Upgraden <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    )}

                    {subStatus && subStatus.effectivePlan !== 'free' && canManageBilling && (
                      <button
                        type="button"
                        onClick={handlePortal}
                        disabled={portalLoading}
                        className={`mt-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors disabled:opacity-60 ${darkMode ? 'border-slate-600 text-slate-100 hover:bg-slate-700' : 'border-slate-300 text-slate-800 hover:bg-slate-100'}`}
                      >
                        {portalLoading ? 'Wird geladen…' : 'Abo verwalten'}
                      </button>
                    )}
                    {subStatus && subStatus.effectivePlan !== 'free' && !canManageBilling && (
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        Nur der Team-Inhaber oder Team-Admin kann das Abo verwalten.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {activeTab === 'notifications' && (
          /* Benachrichtigungen */
          <div className={cardClasses}>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-amber-900/30' : 'bg-amber-100'}`}>
                <Bell className={`w-5 h-5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
              </div>
              <div className="flex-1">
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Benachrichtigungen</h2>
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Verwalten Sie Ihre Benachrichtigungseinstellungen</p>
              </div>
              {!editingNotif && (
                <button
                  type="button"
                  onClick={() => { setEditingNotif(true); setErrorMessage(''); setSuccessMessage(''); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Bearbeiten
                </button>
              )}
            </div>
            <div className="space-y-4">
              <label className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${editingNotif ? 'cursor-pointer' : 'cursor-default opacity-60'} ${notifySignedUploadEmail ? (darkMode ? 'border-blue-700 bg-blue-900/20' : 'border-blue-200 bg-blue-50') : (darkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white')}`}>
                <input
                  type="checkbox"
                  checked={notifySignedUploadEmail}
                  onChange={e => setNotifySignedUploadEmail(e.target.checked)}
                  disabled={!editingNotif}
                  className={`w-4 h-4 mt-0.5 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500 ${!editingNotif ? 'cursor-not-allowed' : ''}`}
                />
                <div>
                  <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                    E-Mail wenn Kunde Dokumente hochlädt
                  </p>
                  <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Sie erhalten eine E-Mail, sobald der Kunde die unterschriebenen Dokumente hochgeladen hat. Der Status im Tool wird immer aktualisiert.
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${editingNotif ? 'cursor-pointer' : 'cursor-default opacity-60'} ${notifyPayoutDigest ? (darkMode ? 'border-blue-700 bg-blue-900/20' : 'border-blue-200 bg-blue-50') : (darkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white')}`}>
                <input
                  type="checkbox"
                  checked={notifyPayoutDigest}
                  onChange={e => setNotifyPayoutDigest(e.target.checked)}
                  disabled={!editingNotif}
                  className={`w-4 h-4 mt-0.5 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500 ${!editingNotif ? 'cursor-not-allowed' : ''}`}
                />
                <div>
                  <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                    Wöchentliche Erfolgsmeldung (freitags)
                  </p>
                  <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Sammel-Mail mit den seit dem letzten Mal erfolgreichen BAFA-Vorgängen (Zahlung erfolgt/Ausgezahlt/Abgeschlossen), ohne Beträge.
                    {istTeamInhaber ? '' : ' Team-Mitglieder erhalten sie nicht selbst — sie geht an den Team-Inhaber.'}
                  </p>
                </div>
              </label>

              {/* Antwortadresse für Kundenmails — Team-weit, nur der Inhaber ändert sie.
                  Mitglieder sehen den eingestellten Wert, damit klar ist, wohin
                  Kundenantworten laufen. */}
              <div className={`p-3 rounded-lg border ${darkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
                <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                  Antwortadresse für Kundenmails
                </p>
                <p className={`text-xs mt-0.5 mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Antwortet ein Kunde auf eine Mail aus dem Programm, geht die Antwort an diese
                  Adresse. Ohne Eintrag an die E-Mail des Beraters, der die Mail ausgelöst hat.
                  {istTeamInhaber ? '' : ' Nur der Team-Inhaber kann sie ändern.'}
                </p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={replyToEmail}
                    onChange={e => setReplyToEmail(e.target.value)}
                    disabled={!istTeamInhaber || replyToSaving}
                    placeholder={user?.email || 'name@firma.de'}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-slate-900 border-slate-700 text-slate-200' : 'bg-white border-slate-300 text-slate-900'} ${!istTeamInhaber ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  {istTeamInhaber && (
                    <button
                      type="button"
                      disabled={replyToSaving || replyToEmail === replyToOriginal}
                      onClick={async () => {
                        setReplyToSaving(true); setErrorMessage(''); setSuccessMessage('');
                        try {
                          const res = await api.patch<{ replyToEmail: string | null }>('/api/team/reply-to', { replyToEmail });
                          setReplyToOriginal(res.replyToEmail ?? '');
                          setReplyToEmail(res.replyToEmail ?? '');
                          setSuccessMessage(res.replyToEmail
                            ? `Kundenantworten gehen künftig an ${res.replyToEmail}`
                            : 'Antwortadresse entfernt — Antworten gehen an den jeweiligen Berater');
                        } catch (err: unknown) {
                          setErrorMessage(err instanceof Error ? err.message : 'Fehler beim Speichern');
                        } finally {
                          setReplyToSaving(false);
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {replyToSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Speichern
                    </button>
                  )}
                </div>
              </div>

              {/* Team-weite Benachrichtigung bei nicht zugeordneten Mails — nur der Inhaber
                  ändert sie, Mitglieder sehen den Stand (read-only). */}
              <div className={`p-3 rounded-lg border ${darkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
                <label className={`flex items-start gap-3 ${istTeamInhaber ? 'cursor-pointer' : 'cursor-default opacity-60'}`}>
                  <input
                    type="checkbox"
                    checked={notifyUnassignedMail}
                    onChange={e => setNotifyUnassignedMail(e.target.checked)}
                    disabled={!istTeamInhaber || notifyUnassignedMailSaving}
                    className={`w-4 h-4 mt-0.5 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500 ${!istTeamInhaber ? 'cursor-not-allowed' : ''}`}
                  />
                  <div>
                    <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                      E-Mail ans ganze Team, wenn eine Mail nicht zugeordnet werden konnte
                    </p>
                    <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Jedes Team-Mitglied bekommt eine Mail, sobald der Mail-Monitor eine eingehende
                      Mail keinem Kunden zuordnen konnte.
                      {istTeamInhaber ? '' : ' Nur der Team-Inhaber kann das ändern.'}
                    </p>
                  </div>
                </label>
                {istTeamInhaber && (
                  <div className="flex justify-end mt-3">
                    <button
                      type="button"
                      disabled={notifyUnassignedMailSaving || notifyUnassignedMail === notifyUnassignedMailOriginal}
                      onClick={async () => {
                        setNotifyUnassignedMailSaving(true); setErrorMessage(''); setSuccessMessage('');
                        try {
                          const res = await api.patch<{ notifyUnassignedMail: boolean }>('/api/team/notify-unassigned-mail', { notifyUnassignedMail });
                          setNotifyUnassignedMailOriginal(res.notifyUnassignedMail);
                          setNotifyUnassignedMail(res.notifyUnassignedMail);
                          setSuccessMessage(res.notifyUnassignedMail
                            ? 'Benachrichtigung bei nicht zugeordneten Mails aktiviert'
                            : 'Benachrichtigung bei nicht zugeordneten Mails deaktiviert');
                        } catch (err: unknown) {
                          setErrorMessage(err instanceof Error ? err.message : 'Fehler beim Speichern');
                        } finally {
                          setNotifyUnassignedMailSaving(false);
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {notifyUnassignedMailSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Speichern
                    </button>
                  </div>
                )}
              </div>

              {/* Ausgangs-Postfach: Versand über den eigenen Mailserver. Nur so steht die
                  eigene Domain im Absender, ohne DNS-Freigabe für einen fremden Dienst. */}
              {istTeamInhaber && (
                <div className={`p-3 rounded-lg border ${darkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
                  <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                    Eigenes Postfach für den Versand
                  </p>
                  <p className={`text-xs mt-0.5 mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Ohne Eintrag verschickt das Programm Kundenmails über den Versanddienst. Mit
                    hinterlegtem Postfach gehen sie über euren eigenen Mailserver — dann steht eure
                    eigene Adresse im Absender. Schlägt der Versand fehl, geht die Mail automatisch
                    über den Versanddienst raus, damit sie nicht verloren geht.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      ['fromEmail', 'Absenderadresse', 'team@firma.de', 'col-span-2'],
                      ['fromName', 'Anzeigename (optional)', 'Firma', 'col-span-2'],
                      ['host', 'Server', 'mail.firma.de', ''],
                      ['port', 'Port', '465', ''],
                      ['username', 'Benutzername', 'team@firma.de', ''],
                      ['password', outgoing?.hasPassword ? 'Passwort (leer = unverändert)' : 'Passwort', '••••••••', ''],
                    ] as [string, string, string, string][]).map(([key, label, ph, span]) => (
                      <div key={key} className={span}>
                        <label className={`block text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{label}</label>
                        <input
                          type={key === 'password' ? 'password' : key === 'port' ? 'number' : 'text'}
                          value={(outForm as any)[key]}
                          placeholder={ph}
                          onChange={e => setOutForm({ ...outForm, [key]: key === 'port' ? Number(e.target.value) : e.target.value })}
                          className={`w-full px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-slate-900 border-slate-700 text-slate-200' : 'bg-white border-slate-300 text-slate-900'}`}
                        />
                      </div>
                    ))}
                  </div>

                  {outTestResult && (
                    <p className={`text-xs mt-2 ${outTestResult.ok ? 'text-green-600' : 'text-rose-600'}`}>
                      {outTestResult.ok ? '✓ Anmeldung am Postfach erfolgreich' : `✗ ${outTestResult.error}`}
                    </p>
                  )}
                  {!outTestResult && outgoing?.lastTestStatus && (
                    <p className={`text-xs mt-2 ${outgoing.lastTestStatus === 'ok' ? 'text-green-600' : 'text-rose-600'}`}>
                      {outgoing.lastTestStatus === 'ok' ? '✓ Zuletzt erfolgreich geprüft' : `✗ ${outgoing.lastTestError}`}
                    </p>
                  )}

                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      disabled={outSaving}
                      onClick={async () => {
                        setOutSaving(true); setErrorMessage(''); setSuccessMessage(''); setOutTestResult(null);
                        try {
                          const res = await api.put<any>('/api/team/outgoing-mail', outForm);
                          setOutgoing(res);
                          setOutForm({ ...outForm, password: '' });
                          setSuccessMessage('Postfach gespeichert — jetzt Verbindung prüfen');
                        } catch (err: unknown) {
                          setErrorMessage(err instanceof Error ? err.message : 'Fehler beim Speichern');
                        } finally { setOutSaving(false); }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 cursor-pointer"
                    >
                      {outSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Speichern
                    </button>
                    <button
                      type="button"
                      disabled={outTesting || !outgoing}
                      onClick={async () => {
                        setOutTesting(true); setOutTestResult(null);
                        try {
                          setOutTestResult(await api.post<{ ok: boolean; error?: string }>('/api/team/outgoing-mail/test', {}));
                        } catch (err: unknown) {
                          setOutTestResult({ ok: false, error: err instanceof Error ? err.message : 'Test fehlgeschlagen' });
                        } finally { setOutTesting(false); }
                      }}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 cursor-pointer ${darkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                    >
                      {outTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Verbindung prüfen
                    </button>
                    {outgoing && (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await api.delete('/api/team/outgoing-mail');
                            setOutgoing(null); setOutTestResult(null);
                            setOutForm({ fromEmail: '', fromName: '', host: '', port: 465, username: '', password: '' });
                            setSuccessMessage('Postfach entfernt — Versand läuft wieder über den Versanddienst');
                          } catch (err: unknown) {
                            setErrorMessage(err instanceof Error ? err.message : 'Fehler beim Entfernen');
                          }
                        }}
                        className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'} cursor-pointer`}
                      >
                        Entfernen
                      </button>
                    )}
                  </div>
                </div>
              )}

              {editingNotif && (
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => { setNotifySignedUploadEmail((user as any)?.notifySignedUploadEmail !== false); setNotifyPayoutDigest((user as any)?.notifyPayoutDigest !== false); setErrorMessage(''); setEditingNotif(false); }}
                    disabled={saving}
                    className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg font-medium transition-colors cursor-pointer disabled:opacity-50 ${darkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                  >
                    <X className="w-4 h-4" />
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={async () => {
                      setSaving(true);
                      setSuccessMessage('');
                      setErrorMessage('');
                      try {
                        await updateProfile({ notifySignedUploadEmail, notifyPayoutDigest } as any);
                        setSuccessMessage('Einstellungen gespeichert');
                        setEditingNotif(false);
                      } catch (err: unknown) {
                        setErrorMessage(err instanceof Error ? err.message : 'Fehler beim Speichern');
                      } finally {
                        setSaving(false);
                      }
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? 'Wird gespeichert...' : 'Speichern'}
                  </button>
                </div>
              )}
            </div>
          </div>
          )}

          {activeTab === 'mail-monitor' && (
            <MailMonitorSettings darkMode={darkMode} cardClasses={cardClasses} inputClasses={inputClasses} />
          )}

          {activeTab === 'appearance' && (
          /* Darstellung */
          <div className={cardClasses}>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-pink-900/30' : 'bg-pink-100'}`}>
                <Palette className={`w-5 h-5 ${darkMode ? 'text-pink-400' : 'text-pink-600'}`} />
              </div>
              <div className="flex-1">
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Darstellung</h2>
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Anpassen der Benutzeroberfläche</p>
              </div>
              {!editingAppearance && (
                <button
                  type="button"
                  onClick={startAppearanceEdit}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Bearbeiten
                </button>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Design</label>
                <CustomSelect value={editingAppearance ? pendingTheme : themeDisplay} onChange={setPendingTheme} options={['Hell', 'Dunkel', 'System']} disabled={!editingAppearance} />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Editor-Schriftgröße</label>
                <CustomSelect value={editingAppearance ? pendingFontSize : fontSize} onChange={setPendingFontSize} options={['Klein', 'Mittel', 'Groß']} disabled={!editingAppearance} />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Sprache</label>
                <CustomSelect value={editingAppearance ? pendingLanguage : language} onChange={setPendingLanguage} options={['Deutsch', 'English']} disabled={!editingAppearance} />
              </div>
              {editingAppearance && (
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => { setEditingAppearance(false); setErrorMessage(''); }} className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg font-medium transition-colors cursor-pointer ${darkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                    <X className="w-4 h-4" />
                    Abbrechen
                  </button>
                  <button type="button" onClick={saveAppearance} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium cursor-pointer">
                    <Save className="w-4 h-4" />
                    Speichern
                  </button>
                </div>
              )}
            </div>
          </div>
          )}

          {activeTab === 'admin' && user?.isAdmin && (
            /* Admin – Subscription verwalten */
            <div className={`${cardClasses} mt-0`}>
              <h2 className={`font-semibold mb-3 text-base ${darkMode ? 'text-white' : 'text-slate-900'}`}>Admin – Subscription verwalten</h2>
              <div className="flex gap-2 mb-3">
                <input
                  className={`border rounded px-2 py-1 text-sm flex-1 ${darkMode ? 'border-slate-600 bg-slate-700 text-white placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400'}`}
                  placeholder="E-Mail des Users suchen"
                  value={adminTargetEmail}
                  onChange={e => setAdminTargetEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchUserByEmail()}
                />
                <button
                  className={`border rounded px-3 py-1 text-sm ${darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`}
                  onClick={searchUserByEmail}
                >
                  Laden
                </button>
              </div>
              {adminTargetId && (
                <div className="flex flex-col gap-2 text-sm">
                  <label className={`flex flex-wrap items-center gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Plan
                    <select
                      className={`border rounded px-2 py-1 ${darkMode ? 'border-slate-600 bg-slate-700 text-white' : 'border-slate-300 bg-white text-slate-900'}`}
                      value={adminPlan}
                      onChange={e => setAdminPlan(e.target.value)}
                    >
                      <option value="free">Free</option>
                      <option value="pro">Pro (Legacy)</option>
                      <option value="unlimited">Unlimited (Legacy)</option>
                      <option value="starter">Starter</option>
                      <option value="professional">Professional</option>
                      <option value="business">Business</option>
                      <option value="flat">Flat</option>
                    </select>
                  </label>
                  <label className={`flex flex-wrap items-center gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Plan läuft ab
                    <input
                      type="date"
                      className={`border rounded px-2 py-1 ${darkMode ? 'border-slate-600 bg-slate-700 text-white' : 'border-slate-300 bg-white text-slate-900'}`}
                      value={adminPlanExpiresAt}
                      onChange={e => setAdminPlanExpiresAt(e.target.value)}
                    />
                  </label>
                  <label className={`flex flex-wrap items-center gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Bericht-Credits
                    <input
                      type="number"
                      min={0}
                      className={`border rounded px-2 py-1 w-20 ${darkMode ? 'border-slate-600 bg-slate-700 text-white' : 'border-slate-300 bg-white text-slate-900'}`}
                      value={adminReportCredits}
                      onChange={e => setAdminReportCredits(Number(e.target.value))}
                    />
                  </label>
                  <label className={`flex flex-wrap items-center gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <input
                      type="checkbox"
                      checked={adminStartklar}
                      onChange={e => setAdminStartklar(e.target.checked)}
                    />
                    Startklar-Programm aktiv (Pro-Zugang)
                  </label>
                  {adminStartklar && (
                    <label className={`flex flex-wrap items-center gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      Startklar-Programm läuft ab
                      <input
                        type="date"
                        className={`border rounded px-2 py-1 ${darkMode ? 'border-slate-600 bg-slate-700 text-white' : 'border-slate-300 bg-white text-slate-900'}`}
                        value={adminStartklarExpiresAt}
                        onChange={e => setAdminStartklarExpiresAt(e.target.value)}
                      />
                    </label>
                  )}
                  <label className={`flex flex-wrap items-center gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Max Berichte Override
                    <input
                      type="number"
                      min={0}
                      className={`border rounded px-2 py-1 w-24 ${darkMode ? 'border-slate-600 bg-slate-700 text-white' : 'border-slate-300 bg-white text-slate-900'}`}
                      value={adminMaxReports ?? ''}
                      onChange={e => setAdminMaxReports(e.target.value ? Number(e.target.value) : null)}
                    />
                    <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>(leer = Standardlimit)</span>
                  </label>
                  <label className="flex flex-wrap items-center gap-2">
                    Max Seats Override
                    <input
                      type="number"
                      min={0}
                      className="border rounded px-2 py-1 w-24 bg-background"
                      value={adminMaxSeats ?? ''}
                      onChange={e => setAdminMaxSeats(e.target.value ? Number(e.target.value) : null)}
                    />
                    <span className="text-muted-foreground text-xs">(leer = Standardlimit)</span>
                  </label>
                  <button
                    className="border rounded px-3 py-1 text-sm self-start bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
                    onClick={saveAdminSubscription}
                    disabled={adminSubSaving}
                  >
                    {adminSubSaving ? 'Speichern…' : 'Speichern'}
                  </button>
                  {adminSubMessage && (
                    <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{adminSubMessage}</p>
                  )}
                </div>
              )}
              {!adminTargetId && adminSubMessage && (
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{adminSubMessage}</p>
              )}
            </div>
          )}

          {activeTab === 'account' && (
          /* Konto-Loeschung beantragen */
          <div className="rounded-lg border-2 border-amber-300 dark:border-amber-800 p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Konto-Löschung</h2>
            </div>
            <p className={`text-sm mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Sie haben gemäß Art. 17 DSGVO jederzeit das Recht, die Löschung Ihres Kontos zu verlangen. Die Ausführung erfolgt durch unseren Administrator.
            </p>
            <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Nach Eingang Ihrer Anfrage wird Ihr Konto gesperrt und nach einer Aufbewahrungsfrist von 30&nbsp;Tagen endgültig gelöscht. Während dieser Frist ist eine Wiederherstellung auf Wunsch möglich.
            </p>
            <a
              href={deletionRequestMailto}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium"
            >
              <Mail className="w-4 h-4" />
              Löschung beantragen
            </a>
          </div>
          )}
          {activeTab === 'api' && (
            <div className="space-y-6">
              <ApiSettings darkMode={darkMode} cardClasses={cardClasses} inputClasses={inputClasses} />
              <AutomationSettings darkMode={darkMode} cardClasses={cardClasses} inputClasses={inputClasses} />
            </div>
          )}

          {activeTab === 'team' && (
            <div className="space-y-6">
            {subStatus?.features?.team ? (
              <TeamSettings
                darkMode={darkMode}
                cardClasses={cardClasses}
                inputClasses={inputClasses}
              />
            ) : (
              <div className={cardClasses}>
                <div className="flex flex-col items-center text-center py-8 gap-4">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center ${darkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                    <Users className={`w-7 h-7 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                  </div>
                  <div>
                    <h3 className={`text-lg font-semibold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Team-Funktionen</h3>
                    <p className={`text-sm max-w-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Laden Sie Kollegen ein, teilen Sie Berichte und verwalten Sie Ihr Team gemeinsam. Verfügbar mit der <strong>Teamoption</strong> (+50 € / Monat, bis zu 5 Mitglieder) zu jedem Tarif dazubuchbar.
                    </p>
                  </div>
                  <Link to="/preise" className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors text-sm">
                    Teamoption dazubuchen
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            )}

            {/* Dateiname-Muster (aus Profil hierher verschoben) */}
            {(() => {
              const VARS: { key: string; label: string }[] = [
                { key: '{Kundenname}', label: 'Kundenname' },
                { key: '{AZ}', label: 'Vorgangsnummer' },
                { key: '{Datum}', label: 'Datum' },
                { key: '{Typ}', label: 'Dokumenttyp' },
              ];
              const PRESETS: { label: string; blocks: string[] }[] = [
                { label: 'Standard (bisherige Namen)', blocks: [] },
                { label: 'Dokumenttyp', blocks: ['{Typ}'] },
                { label: 'Kundenname + Dokumenttyp', blocks: ['{Kundenname}', '{Typ}'] },
                { label: 'Vorgangsnummer + Dokumenttyp', blocks: ['{AZ}', '{Typ}'] },
                { label: 'Kundenname + Vorgangsnummer + Dokumenttyp', blocks: ['{Kundenname}', '{AZ}', '{Typ}'] },
                { label: 'Alles', blocks: ['{Kundenname}', '{AZ}', '{Datum}', '{Typ}'] },
              ];
              const moveBlock = (i: number, dir: -1 | 1) => {
                const next = [...filenameBlocks];
                [next[i], next[i + dir]] = [next[i + dir], next[i]];
                setFilenameBlocks(next);
              };
              const removeBlock = (i: number) => setFilenameBlocks(filenameBlocks.filter((_, idx) => idx !== i));
              const addBlock = (key: string) => setFilenameBlocks([...filenameBlocks, key]);
              const today = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
              const sanitize = (s: string) => s.replace(/[^a-zA-Z0-9äöüÄÖÜß_\-]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
              const previewFile = (typ: string) => {
                if (filenameBlocks.length === 0) return `${typ}.pdf`;
                return sanitize(filenameBlocks.join('_')
                  .replace(/\{Kundenname\}/g, 'Mustermann_GmbH')
                  .replace(/\{Datum\}/g, today.replace(/\./g, '-'))
                  .replace(/\{AZ\}/g, '2025-001')
                  .replace(/\{Typ\}/g, typ)
                ) + '.pdf';
              };
              const availableVars = VARS.filter(v => !filenameBlocks.includes(v.key));
              const blockLabel = (key: string) => VARS.find(v => v.key === key)?.label ?? key;
              return (
                <div className={cardClasses}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                      <Palette className={`w-5 h-5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                    </div>
                    <div className="flex-1">
                      <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Dateiname-Muster</h2>
                      <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Eigene Struktur für generierte Dateinamen</p>
                    </div>
                    {!editingFilename && (
                      <button
                        type="button"
                        onClick={() => { setEditingFilename(true); setErrorMessage(''); setSuccessMessage(''); }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Bearbeiten
                      </button>
                    )}
                  </div>
                  <div className="space-y-5">

                    {/* Vorlage — nur im Bearbeiten-Modus */}
                    {editingFilename && (
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Schnellauswahl</label>
                      <select
                        className={inputClasses}
                        value={JSON.stringify(filenameBlocks)}
                        onChange={e => {
                          const preset = PRESETS.find(p => JSON.stringify(p.blocks) === e.target.value);
                          if (preset) setFilenameBlocks(preset.blocks);
                        }}
                      >
                        {PRESETS.map(p => (
                          <option key={p.label} value={JSON.stringify(p.blocks)}>{p.label}</option>
                        ))}
                        {!PRESETS.some(p => JSON.stringify(p.blocks) === JSON.stringify(filenameBlocks)) && (
                          <option value={JSON.stringify(filenameBlocks)}>Eigene Auswahl</option>
                        )}
                      </select>
                    </div>
                    )}

                    {/* Bausteine */}
                    <div>
                      <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Reihenfolge</label>
                      {filenameBlocks.length === 0 ? (
                        <p className={`text-sm italic ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Standard — bisherige Dateinamen werden verwendet</p>
                      ) : (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {filenameBlocks.map((key, i) => (
                            <div key={i} className="flex items-center gap-0">
                              {i > 0 && <span className={`text-sm font-mono mr-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>_</span>}
                              <div className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-sm font-medium ${darkMode ? 'bg-purple-900/40 border border-purple-700 text-purple-200' : 'bg-purple-100 border border-purple-200 text-purple-800'}`}>
                                {editingFilename && <button type="button" disabled={i === 0} onClick={() => moveBlock(i, -1)} className="opacity-50 hover:opacity-100 disabled:opacity-20 text-xs px-0.5">◀</button>}
                                <span>{blockLabel(key)}</span>
                                {editingFilename && <button type="button" disabled={i === filenameBlocks.length - 1} onClick={() => moveBlock(i, 1)} className="opacity-50 hover:opacity-100 disabled:opacity-20 text-xs px-0.5">▶</button>}
                                {editingFilename && <button type="button" onClick={() => removeBlock(i)} className={`ml-1 text-xs hover:text-red-500 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>✕</button>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Hinzufügen — nur im Bearbeiten-Modus */}
                      {editingFilename && availableVars.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {availableVars.map(v => (
                            <button
                              key={v.key}
                              type="button"
                              onClick={() => addBlock(v.key)}
                              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm border border-dashed transition-colors ${darkMode ? 'border-slate-600 text-slate-400 hover:border-purple-500 hover:text-purple-300' : 'border-slate-300 text-slate-500 hover:border-purple-400 hover:text-purple-600'}`}
                            >
                              <span>+</span> {v.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Vorschau */}
                    <div className={`rounded-lg p-3 ${darkMode ? 'bg-slate-700/50 border border-slate-600' : 'bg-slate-50 border border-slate-200'}`}>
                      <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Vorschau mit Beispieldaten:</p>
                      <div className="space-y-1">
                        {[['bericht', 'Bericht'], ['de_minimis_erklaerung', 'De-minimis'], ['vollmacht', 'Vollmacht'], ['grc_erklaerung', 'GRC']].map(([typ, label]) => (
                          <div key={typ} className="flex items-start gap-2">
                            <span className={`text-xs w-20 flex-shrink-0 pt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{label}:</span>
                            <span className={`text-xs font-mono break-all ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{previewFile(typ)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {editingFilename && (
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={cancelFilenameEdit} disabled={savingFilename} className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg font-medium transition-colors cursor-pointer disabled:opacity-50 ${darkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                        <X className="w-4 h-4" />
                        Abbrechen
                      </button>
                      <button type="button" onClick={saveFilenamePattern} disabled={savingFilename} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
                        {savingFilename ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {savingFilename ? 'Wird gespeichert...' : 'Speichern'}
                      </button>
                    </div>
                    )}
                  </div>
                </div>
              );
            })()}
            </div>
          )}
        </div>
          </div>{/* content column */}
        </div>{/* sidebar+content flex */}
      </div>
      <ConfirmDialog
        open={alertMsg !== null}
        title="Hinweis"
        message={alertMsg || ''}
        confirmLabel="OK"
        hideCancel
        onConfirm={() => setAlertMsg(null)}
        onCancel={() => setAlertMsg(null)}
      />
    </div>
  );
}
